import React, { createContext, type FC, useContext, useEffect, useState } from 'react';
import { type WifiNetwork } from '@lib/networkApiTypes';
import { messageBox } from '@src/style/style';
import { WifiApi } from './wifiApi';
import { v4 as uuid } from 'uuid';

/**
 * A function that should be called within the useEffect of a react component to ensure proper clean up upon unmount
 */
export type cleanupFunction = () => void;
type UUID = string;

interface WifiNetworksContextData {
  connectedWifiNetwork: WifiNetwork | undefined
  wifiNetworks: WifiNetwork[]

  fetchNetworks: () => void
  isFetchingNetworks: boolean

  connectToWifi: (wifiNetwork: WifiNetwork, password: string) => Promise<boolean>
  isConnectingToWifi: boolean

  disconnectFromWifi: () => Promise<boolean>
  isDisconnectingFromWifi: boolean

  /**
   * @returns function to remove a component from the requester list for network polling
   */
  requestNetworkPolling: () => cleanupFunction
}

const WifiNetworksContext = createContext<WifiNetworksContextData>({
  connectedWifiNetwork: undefined,
  wifiNetworks: [],
  fetchNetworks: () => {},
  isFetchingNetworks: false,
  connectToWifi: async () => false,
  isConnectingToWifi: false,
  disconnectFromWifi: async () => false,
  isDisconnectingFromWifi: false,
  requestNetworkPolling: () => () => {},
});

interface WifiNetworksProviderProps {
  isPendantAvailable: boolean
}

export const WifiNetworksProvider: FC<WifiNetworksProviderProps> = ({ isPendantAvailable, children }) => {
  if (!isPendantAvailable) {
    return <> {children} </>;
  }

  const [connectedWifiNetwork, setConnectedWifiNetwork] = useState<WifiNetwork | undefined>(undefined);
  const [wifiNetworks, setWifiNetworks] = useState<WifiNetwork[]>([]);
  const [isFetchingNetworks, setIsFetchingNetworks] = useState(false);
  const [wifiConnectionPromise, setWifiConnectionPromise] = useState<Promise<boolean> | undefined>(undefined);
  const [wifiDisconnectionPromise, setWifiDisconnectionPromise] = useState<Promise<boolean> | undefined>(undefined);
  const [networkPollRequesters, setNetworkPollRequesters] = useState<UUID[]>([]);
  const [shouldPollNetworks, setShouldPollNetworks] = useState<boolean>(false);

  const fetchNetworks = (): void => {
    setIsFetchingNetworks(true);
    setWifiNetworks([]);

    WifiApi.fetchNetworks()
      .then(fetchNetworksResult => {
        setConnectedWifiNetwork(fetchNetworksResult.connectedNetwork);
        setWifiNetworks(fetchNetworksResult.networks);
      })
      .catch((errorMessage: string) => {
        console.error(errorMessage);
        messageBox(errorMessage, 'error');

        setConnectedWifiNetwork(undefined);
        setWifiNetworks([]);
      })
      .finally(() => { setIsFetchingNetworks(false); });
  };

  const requestNetworkPolling = (): cleanupFunction => {
    const requesterID: UUID = uuid();
    setNetworkPollRequesters(networkPollRequesters => [...networkPollRequesters, requesterID]);

    const removeRequester: cleanupFunction = (): void => {
      setNetworkPollRequesters(networkPollRequesters => networkPollRequesters.filter((id) => { return id !== requesterID; }));
    };

    return removeRequester;
  };

  const pollNetworks = (): cleanupFunction => {
    const networkPollingCleanUp: cleanupFunction = WifiApi.pollNetworks(
      5000,
      (networkResult) => {
        setConnectedWifiNetwork(networkResult.connectedNetwork);
        setWifiNetworks(networkResult.networks);
      },
      (_xhr, status, _error) => {
        if (status === 'abort') {
          // Don't change the state if the request was aborted
          return;
        }

        setConnectedWifiNetwork(undefined);
        setWifiNetworks([]);
      },
    );
    return networkPollingCleanUp;
  };

  useEffect(() => {
    fetchNetworks();
  }, []);

  useEffect(() => {
    if (networkPollRequesters.length > 0) {
      setShouldPollNetworks(true);
    }

    else {
      setShouldPollNetworks(false);
    }
  }, [networkPollRequesters]);

  useEffect(() => {
    if (shouldPollNetworks) {
      return pollNetworks();
    }
  }, [shouldPollNetworks]);

  const connectToWifi = async (wifiNetwork: WifiNetwork, password: string): Promise<boolean> => {
    messageBox('WiFi connection in progress, this might take some time.', 'info');

    if (wifiConnectionPromise !== undefined) {
      throw new Error('Cannot connect to wifi, another connection request is in progress.');
    }

    const newWifiConnectionPromise = WifiApi.connect(wifiNetwork, password)
      .then(() => {
        setConnectedWifiNetwork(wifiNetwork);
        setWifiConnectionPromise(undefined);

        return true;
      })
      .catch((errorMessage: string) => {
        console.error(errorMessage);
        messageBox(`Connection to wifi "${wifiNetwork.ssid}" failed.`, 'error');

        setWifiConnectionPromise(undefined);

        return false;
      });

    setWifiConnectionPromise(newWifiConnectionPromise);

    return await newWifiConnectionPromise;
  };

  const disconnectFromWifi = async (): Promise<boolean> => {
    if (connectedWifiNetwork === undefined) {
      return true;
    }

    const newWifiDisconnectionPromise = WifiApi.disconnect()
      .then(() => {
        setConnectedWifiNetwork(undefined);
        setWifiDisconnectionPromise(undefined);

        return true;
      })
      .catch((errorMessage: string) => {
        console.error(errorMessage);
        messageBox(`Disconnection from wifi "${connectedWifiNetwork.ssid}" failed.`, 'error');

        setWifiDisconnectionPromise(undefined);

        return false;
      });

    setWifiDisconnectionPromise(newWifiDisconnectionPromise);

    return await newWifiDisconnectionPromise;
  };

  return (
    <WifiNetworksContext.Provider value={{
      connectedWifiNetwork,
      wifiNetworks,
      isFetchingNetworks,
      fetchNetworks,
      connectToWifi,
      isConnectingToWifi: Boolean(wifiConnectionPromise),
      disconnectFromWifi,
      isDisconnectingFromWifi: Boolean(wifiDisconnectionPromise),
      requestNetworkPolling,
    }}>
      {children}
    </WifiNetworksContext.Provider>
  );
};

export const useWifiNetworks = (): WifiNetworksContextData => useContext(WifiNetworksContext);
